import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Popper } from '@mui/material';
import moment from 'moment';
import { Images } from 'utils';
import { SimpleTooltip } from 'components';
import { systemActions } from 'store';

export const Eligibility = ({ info, payerName, payerShortName, fromClient }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popper' : undefined;
    const dispatch = useDispatch();
    const eligibilityConfig = useSelector((state) => state.system.eligibilityAutoCheckConfig);

    useEffect(() => {
        if (!eligibilityConfig) {
            dispatch(systemActions.getEligibilityAutoCheckConfig());
        }
    }, []);

    const handleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    // "Needs Review" when the last check is old: with automatic checks on, older than their frequency; with manual
    // checks, older than the org's reminder (Settings → Eligibility Verification, default 3 days; null = reminder off).
    const checkExpired = (date) => {
        let staleDays = 3;
        if (eligibilityConfig?.enabled) {
            staleDays = eligibilityConfig.frequencyDays;
        } else if (eligibilityConfig) {
            if (eligibilityConfig.staleAfterDays === null) return false;
            staleDays = eligibilityConfig.staleAfterDays || 3;
        }
        const threshold = moment().subtract(staleDays, 'days');
        return moment(date).isBefore(threshold);
    };

    const expired = checkExpired(info?.eligibilityCheck?.requestedDate);
    const hasErrors = !!info?.eligibilityCheck?.errors?.length;
    const isWarning = !info?.eligibilityCheck?.eligible && hasErrors;
    const isMismatch = !!info?.eligibilityCheck?.payerMismatch;
    const hasOtherPrimaryPayer = !!info?.eligibilityCheck?.otherPayer?.isPrimary;

    const renderButtonView = () => {
        if (isMismatch) {
            return fromClient ? (
                <div style={{ background: '#FFFAEB', color: '#B54708' }} className="eligible-status-badge">
                    <img src={Images.eligibilityExpired} alt="icon" />
                    <p>Payer Mismatch</p>
                </div>
            ) : (
                <img src={Images.eligibilityExpired} alt="icon" />
            );
        }
        if (hasOtherPrimaryPayer) {
            return fromClient ? (
                <div style={{ background: '#FEF3F2', color: '#B42318' }} className="eligible-status-badge">
                    <img src={Images.eligibilityFail} alt="icon" />
                    <p>COB: Other Primary</p>
                </div>
            ) : (
                <img src={Images.eligibilityFail} alt="icon" />
            );
        }
        if (fromClient) {
            if (info?.eligibilityCheck?.eligible) {
                return (
                    <div
                        style={{
                            background: expired ? '#FFFAEB' : '#ECFDF3',
                            color: expired ? '#B54708' : '#027A48',
                        }}
                        className="eligible-status-badge"
                    >
                        <img src={expired ? Images.eligibilityExpired : Images.eligibilityChecked} alt="icon" />
                        <p>{expired ? 'Needs Review' : 'Eligible'}</p>
                    </div>
                );
            }
            if (isWarning) {
                return (
                    <div style={{ background: '#FFFAEB', color: '#B54708' }} className="eligible-status-badge">
                        <img src={Images.eligibilityExpired} alt="icon" />
                        <p>Warning</p>
                    </div>
                );
            }
            return (
                <div style={{ background: '#FEF3F2', color: '#B42318' }} className="eligible-status-badge">
                    <img src={Images.eligibilityFail} alt="icon" />
                    <p>Not Eligible</p>
                </div>
            );
        } else {
            if (info?.eligibilityCheck?.eligible) {
                return <img src={expired ? Images.eligibilityExpired : Images.eligibilityChecked} alt="icon" />;
            }
            if (isWarning) {
                return <img src={Images.eligibilityExpired} alt="icon" />;
            }
            return <img src={Images.eligibilityFail} alt="icon" />;
        }
    };
    return (
        <div>
            {info?.eligibilityCheck && (
                <button className="reset-btn-by-default" aria-describedby={id} onClick={handleClick}>
                    {renderButtonView()}
                    {/*<>*/}
                    {/*    {info?.eligibilityCheck?.eligible &&*/}
                    {/*        <img src={expired ? Images.eligibilityExpired : Images.eligibilityChecked} alt="icon" />*/}
                    {/*    }*/}
                    {/*    {!info?.eligibilityCheck?.eligible &&*/}
                    {/*        <img src={Images.eligibilityFail} alt="icon" />*/}
                    {/*    }*/}
                    {/*</>*/}
                </button>
            )}

            <Popper
                id={id}
                open={open}
                anchorEl={anchorEl}
                className="eligibility-info-box"
                placement="bottom-start"
                style={{ position: 'absolute', zIndex: 1500 }}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                {isMismatch ? (
                    <div>
                        <div className="eligibility-info-header">
                            <p className="eligibility-info-title">Eligibility Status:</p>
                            <div
                                style={{ background: '#FFFAEB', color: '#B54708' }}
                                className="eligible-status-badge"
                            >
                                <img src={Images.eligibilityExpired} alt="icon" />
                                <p>Payer Mismatch</p>
                            </div>
                        </div>
                        <div className="eligibility-info-body">
                            <p className="eligibility-fail-text" style={{ color: '#B54708' }}>
                                The member ID is valid but belongs to a different payer
                                {info.eligibilityCheck.payerName
                                    ? ` (${info.eligibilityCheck.payerName})`
                                    : ''}
                                . Please verify the client's insurance information.
                            </p>
                        </div>
                    </div>
                ) : hasOtherPrimaryPayer ? (
                    <div>
                        <div className="eligibility-info-header">
                            <p className="eligibility-info-title">Eligibility Status:</p>
                            <div
                                style={{ background: '#FEF3F2', color: '#B42318' }}
                                className="eligible-status-badge"
                            >
                                <img src={Images.eligibilityFail} alt="icon" />
                                <p>COB: Other Primary</p>
                            </div>
                        </div>
                        <div className="eligibility-info-body">
                            <p className="eligibility-fail-text" style={{ color: '#B42318' }}>
                                {info.eligibilityCheck.payerName || 'This payer'} confirmed active coverage but
                                indicates another payer is primary. Bill the primary payer first.
                            </p>
                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <div style={{ display: 'flex', gap: 4 }}>
                                    <p className="eligibility-info-title">Primary Payer:</p>
                                    <p className="eligibility-checked-date">
                                        {info.eligibilityCheck.otherPayer.payerName}
                                    </p>
                                </div>
                                {info.eligibilityCheck.otherPayer.memberId && (
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <p className="eligibility-info-title">Member ID:</p>
                                        <p className="eligibility-checked-date">
                                            {info.eligibilityCheck.otherPayer.memberId}
                                        </p>
                                    </div>
                                )}
                                {info.eligibilityCheck.otherPayer.policyNumber && (
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <p className="eligibility-info-title">Policy #:</p>
                                        <p className="eligibility-checked-date">
                                            {info.eligibilityCheck.otherPayer.policyNumber}
                                        </p>
                                    </div>
                                )}
                                {info.eligibilityCheck.otherPayer.cobDate && (
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <p className="eligibility-info-title">COB Effective:</p>
                                        <p className="eligibility-checked-date">
                                            {moment(info.eligibilityCheck.otherPayer.cobDate, 'YYYYMMDD').format(
                                                'MMM DD, YYYY',
                                            )}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : isWarning ? (
                    <div>
                        <div className="eligibility-info-header">
                            <p className="eligibility-info-title">Eligibility Status:</p>
                            <div
                                style={{ background: '#FFFAEB', color: '#B54708' }}
                                className="eligible-status-badge"
                            >
                                <img src={Images.eligibilityExpired} alt="icon" />
                                <p>Warning</p>
                            </div>
                        </div>
                        <div className="eligibility-info-body">
                            {info.eligibilityCheck.errors.map((err, idx) => (
                                <p key={idx} className="eligibility-fail-text" style={{ color: '#B54708' }}>
                                    {err}
                                </p>
                            ))}
                        </div>
                    </div>
                ) : !info?.eligibilityCheck?.eligible ? (
                    <div>
                        <div className="eligibility-info-header">
                            <p className="eligibility-info-title">Eligibility Status:</p>
                            <div
                                style={{
                                    background: '#FEF3F2',
                                    color: '#B42318',
                                }}
                                className="eligible-status-badge"
                            >
                                <img src={Images.eligibilityFail} alt="icon" />
                                <p>Not Eligible</p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={{ maxHeight: 500, overflow: 'auto' }}>
                        <div className="eligibility-info-header">
                            <p className="eligibility-info-title">Eligibility Status:</p>
                            <div
                                style={{
                                    background: expired ? '#FFFAEB' : '#ECFDF3',
                                    color: expired ? '#B54708' : '#027A48',
                                }}
                                className="eligible-status-badge"
                            >
                                <img src={expired ? Images.eligibilityExpired : Images.eligibilityChecked} alt="icon" />
                                <p>{expired ? 'Needs Review' : 'Eligible'}</p>
                            </div>
                        </div>

                        <div className="eligibility-info-body">
                            <div className="eligibility-payer-info">
                                <div>
                                    <p className="eligibility-info-title" style={{ marginBottom: 4 }}>
                                        Payer:
                                    </p>
                                    <div className="option-payer-wrapper">
                                        <img src={Images.businessCenter} alt="businessCenter" />
                                        <SimpleTooltip title={<p>{info?.payerName}</p>} placement="top-end">
                                            <p style={{ width: '45px' }}>
                                                {payerShortName || payerName
                                                    ? payerShortName
                                                        ? payerShortName
                                                        : payerName
                                                          ? `${payerName?.slice(0, 5)}.`
                                                          : ''
                                                    : '--'}
                                                {/*{info?.payerShortName ? info?.payerShortName : `${info?.payerName?.slice(0, 5)}.`}*/}
                                            </p>
                                        </SimpleTooltip>
                                    </div>
                                </div>
                                {info?.eligibilityCheck?.payerName && (
                                    <div>
                                        <p className="eligibility-info-title" style={{ marginBottom: 4 }}>
                                            Verified Payer:
                                        </p>
                                        <p className="eligibility-checked-date">{info.eligibilityCheck.payerName}</p>
                                    </div>
                                )}
                                <div>
                                    <p className="eligibility-info-title" style={{ marginBottom: 4 }}>
                                        Last Checked:
                                    </p>
                                    <p className="eligibility-checked-date">
                                        {moment.utc(info?.eligibilityCheck?.requestedDate).format('MMM DD, YYYY')}
                                    </p>
                                </div>
                            </div>

                            {!!info?.eligibilityCheck?.benefits?.length &&
                                info?.eligibilityCheck?.benefits?.map((benefit, index) => (
                                    <div key={index} className="eligibility-info-wrapper">
                                        <p
                                            className="eligibility-info-title"
                                            style={{ marginBottom: 4 }}
                                        >{`Code: ${benefit?.meaning} (${benefit?.code})`}</p>
                                        {!!benefit?.coverages?.length && (
                                            <div className="eligibility-payer-info">
                                                <div>
                                                    <p className="eligibility-info-title" style={{ marginBottom: 4 }}>
                                                        Covered Services:
                                                    </p>
                                                    <div className="eligibility-credentials-info">
                                                        {benefit?.coverages?.map((i, j) => (
                                                            <div key={j}>{i}</div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {!!benefit?.notes?.length && (
                                            <div style={{ maxHeight: '100px', overflow: 'auto' }}>
                                                {benefit?.notes?.map((i, k) => (
                                                    <div className="eligibility-note-info" key={k}>
                                                        <img src={Images.eligibleNotes} alt="noteIcon" />
                                                        <p>{i}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                        </div>
                    </div>
                )}
            </Popper>
            {open && (
                <div
                    className="beck-drop"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setAnchorEl(null);
                    }}
                />
            )}
        </div>
    );
};
